import { useRef, useCallback, useEffect } from "react";
import { fetchAudio } from "../Components/nonUIFuncs";
import { debug } from "../utils/debug";

/**
 * Custom hook for managing audio elements with automatic lifecycle cleanup
 *
 * Benefits:
 * - Lazy loads audio only when first played
 * - Caches audio instances to avoid recreation
 * - Automatically cleans up on component unmount
 * - Prevents memory leaks from accumulated Audio elements
 *
 * @returns {Object} Audio control methods
 * @returns {Function} play - Play an audio file by name
 * @returns {Function} stop - Stop a specific audio file
 * @returns {Function} stopAll - Stop all audio
 * @returns {Function} preload - Preload audio files without playing
 *
 * @param {string} [dataset=""] - Optional dataset prefix. Pass "b_" to route audio to the secondary (Benhti) storage bucket.
 *
 * @example
 * const { play, stopAll } = useAudio();
 * const { play, stopAll } = useAudio(props.dataset); // Minto-aware
 *
 * // Play audio (creates element if needed)
 * play('correctSound.mp3');
 *
 * // Play with callback when finished
 * play('word_hello.mp3', () => {
 *   console.log('Audio finished playing');
 * });
 *
 * // Preload audio for faster playback later
 * useEffect(() => {
 *   preload(['correctSound.mp3', 'incorrectSound.mp3']);
 * }, []);
 */
const useAudio = (dataset = "") => {
  // Cache for audio elements - persists across renders but cleans on unmount
  const audioCache = useRef({});

  /**
   * Play an audio file by name
   * Lazily creates Audio element on first play, then reuses it
   *
   * @param {string} fileName - Name of audio file (e.g., 'correctSound.mp3')
   * @param {Function} onEnded - Optional callback when audio finishes
   * @returns {Promise<void>} Resolves when play starts (or rejects on error)
   */
  const play = useCallback((fileName, onEnded = null) => {
    if (!fileName) {
      debug.warn("useAudio: No fileName provided to play()");
      return Promise.resolve();
    }

    try {
      // Get or create audio element
      if (!audioCache.current[fileName]) {
        const audio = new Audio(fetchAudio(fileName, dataset));

        // Preload metadata for faster playback
        audio.preload = "metadata";

        audioCache.current[fileName] = audio;
      }

      const audio = audioCache.current[fileName];

      // Pause first to avoid race conditions, then reset
      audio.pause();
      audio.currentTime = 0;

      // Remove previous onended listener if exists
      audio.onended = null;

      // Add new callback if provided
      if (onEnded && typeof onEnded === "function") {
        audio.onended = onEnded;
      }

      // Small delay to avoid AbortError from pause/play race condition
      return new Promise((resolve) => {
        setTimeout(() => {
          audio
            .play()
            .then(resolve)
            .catch((err) => {
              // Autoplay policy blocked - this is expected on page load
              if (err.name === "NotAllowedError") {
                debug.log(
                  "Audio autoplay prevented (user interaction required):",
                  fileName,
                );
              } else if (err.name !== "AbortError") {
                // Ignore AbortError (happens when rapidly switching audio)
                debug.error("Audio play failed:", fileName, err);
              }
              resolve();
            });
        }, 10);
      });
    } catch (error) {
      debug.error("Error in useAudio.play():", error);
      return Promise.resolve();
    }
  }, [dataset]);

  /**
   * Stop a specific audio file
   *
   * @param {string} fileName - Name of audio file to stop
   */
  const stop = useCallback((fileName) => {
    const audio = audioCache.current[fileName];
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  }, []);

  /**
   * Stop all currently playing audio
   */
  const stopAll = useCallback(() => {
    Object.values(audioCache.current).forEach((audio) => {
      audio.pause();
      audio.currentTime = 0;
    });
  }, []);

  /**
   * Preload audio files for faster playback later
   * Useful for preloading sound effects or frequently used audio
   *
   * @param {string[]} fileNames - Array of audio file names to preload
   */
  const preload = useCallback((fileNames) => {
    fileNames.forEach((fileName) => {
      if (!audioCache.current[fileName]) {
        const audio = new Audio(fetchAudio(fileName, dataset));
        audio.preload = "metadata";
        audioCache.current[fileName] = audio;
      }
    });
  }, [dataset]);

  // Cleanup on unmount - critical for preventing memory leaks
  useEffect(() => {
    return () => {
      // Stop all audio
      Object.values(audioCache.current).forEach((audio) => {
        audio.pause();
        audio.onended = null;
        audio.src = ""; // Release the audio resource
      });

      // Clear the cache
      audioCache.current = {};
    };
  }, []);

  return {
    play,
    stop,
    stopAll,
    preload,
  };
};

export default useAudio;
